import logo from "@/assets/root-logo.png";
import { useCompanySettings, usePatchCompanySettings } from "@/hooks/useCompanySettings";
import { ImpactMethod } from "@/hooks/useImpactMethod";
import useTranslation from "@/hooks/useTranslation";
import { methodOptions } from "@/pages/settings/ManageMethod";
import { Button, DatePicker, Form, FormProps, Image, message, Radio, Space, Switch } from "antd";
import dayjs, { Dayjs } from "dayjs";
import OnBoardingBanner from "../../assets/OnBoardingBannerImage";
import styles from "./WelcomeScreen.module.scss";

const { RangePicker } = DatePicker;

type FieldType = {
  year_range: Dayjs[];
  fiscal_month: Dayjs;
  include_usage: boolean;
  include_eol: boolean;
  impact_method: ImpactMethod;
};

function WelcomeScreen() {
  const { data: companySettings } = useCompanySettings();
  const patchCompanySettings = usePatchCompanySettings();
  const t = useTranslation();

  const onFinish: FormProps<FieldType>["onFinish"] = async (values) => {
    await patchCompanySettings
      .mutateAsync({
        reporting_start_year: values["year_range"][0].year(),
        reporting_end_year: values["year_range"][1].year(),
        // fiscal_month: values["fiscal_month"].month() + 1,
        include_usage: values["include_usage"],
        include_eol: values["include_eol"],
        impact_method: values["impact_method"],
        show_onboarding: false,
      })
      .then(() => {
        message.success("Settings successfully applied.");
      })
      .catch(() => {
        message.error(t.errors.general);
      });
  };

  return (
    <div className={styles.WelcomeScreen}>
      <div className={styles.header}>
        <div className={styles.logo}>
          <Image
            src={logo}
            preview={false}
            alt="root-logo"
            height={27}
            width={104}
          />
        </div>
        <div className={styles.banner}>
          <OnBoardingBanner />
        </div>
      </div>
      <div className={styles.form}>
        <div className={styles.container}>
          <Form
            layout="horizontal"
            onFinish={onFinish}
            initialValues={{
              year_range: [
                dayjs(String(companySettings?.reporting_start_year), "YYYY"),
                dayjs(String(companySettings?.reporting_end_year), "YYYY"),
              ],
              include_eol: false,
              include_usage: false,
              impact_method: "ReCiPe 2016 v1.03, midpoint (H)",
            }}
          >
            <div className={styles.sections}>
              <div className={styles.headingContainer}>
                <span className={styles.heading}>Welcome to ROOT</span>
              </div>
              <div className={styles.subHeadingContainer}>
                <Space
                  direction={"vertical"}
                  size={1}
                >
                  <span className={styles.subHeading}>Let's get started</span>
                  <span className={styles.description}>
                    Choose the time period, product lifecycle chapters, and impact method for your
                    assessment.
                  </span>
                </Space>
              </div>
              <div className={styles.sectionContainer}>
                <div className={styles.heading}>Scope</div>
                <Space direction={"vertical"}>
                  <span className={styles.description}>Data collection year range</span>
                  <Form.Item
                    name={"year_range"}
                    noStyle
                    rules={[
                      {
                        required: true,
                      },
                    ]}
                  >
                    <RangePicker picker="year" />
                  </Form.Item>
                </Space>
              </div>
              <div className={styles.sectionContainer}>
                <div className={styles.heading}>Product lifecycle</div>
                <Space direction={"vertical"}>
                  <span className={styles.description}>
                    Include the product use phase & end of life in your scope?
                  </span>
                  <Space className={styles.switchBox}>
                    <span className={styles.description}>Usage</span>
                    <Form.Item
                      name={"include_usage"}
                      noStyle
                    >
                      <Switch
                        defaultChecked
                        size={"small"}
                      />
                    </Form.Item>
                    <span className={styles.description}>End of life</span>
                    <Form.Item
                      name={"include_eol"}
                      noStyle
                    >
                      <Switch
                        defaultChecked
                        size={"small"}
                      />
                    </Form.Item>
                  </Space>
                </Space>
              </div>
              <div className={styles.sectionContainer}>
                <div className={styles.heading}>Impact method</div>
                <div className={styles.impactMethodContainer}>
                  <Form.Item
                    name={"impact_method"}
                    noStyle
                  >
                    <Radio.Group
                      style={{
                        display: "flex",
                        flexDirection: "column",
                      }}
                      options={methodOptions}
                    />
                  </Form.Item>
                </div>
              </div>
              <div className={styles.sectionContainer}>
                <Button
                  type={"primary"}
                  htmlType="submit"
                  loading={patchCompanySettings.isLoading}
                >
                  Confirm scope
                </Button>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default WelcomeScreen;
